import { gql, useMutation } from '@apollo/client'
import { useFeatureFlag } from 'featureFlags/hooks/useFeatureFlag'
import { FormikHelpers } from 'formik'
import { pickBy } from 'lodash'
import { useRouter } from 'next/router'
import qs from 'qs'
import { Dispatch } from 'react'
import { FeatureFlag } from 'shared/enums/FeatureFlag'

import Bugsnag from '@bugsnag/js'
import {
  IAction,
  IErrorAction,
  IOrderSuccessAction,
} from 'checkout/containers/CheckoutStateReducer'
import { ORDER_PROPS, PROMOTION_PROPS, SHIPPING_ADDRESS_COUNT } from 'checkout/hooks/useOrder'
import { IOrder, PartialOrder } from 'checkout/types/IOrder'
import { t } from 'localization'
import { addressErrorFieldMap } from 'shared/addressValidations'
import { useAuth } from 'shared/hooks/useAuth'
import { isTimeoutError, timeoutMessage } from '../utils/timeout'

const TP = 'checkout.hooks.useShippingAddresses'

const getConnectAddressToOrderMutation = (promotionsEnabled: boolean) => gql`
  mutation connectAddressToOrder($input: ConnectAddressToOrderInput!, $shippingAddressCount: Int) {
    connectAddressToOrder(input: $input) {
      ...OrderProps
      ${promotionsEnabled ? '...PromotionProps' : ''}
    }
  }
  ${ORDER_PROPS}
  ${promotionsEnabled ? PROMOTION_PROPS : ''}
`

const getCreateShippingAddressMutation = (promotionsEnabled: boolean) => gql`
  mutation createShippingAddress($input: CreateCheckoutShippingAddressInput!, $shippingAddressCount: Int) {
    createCheckoutShippingAddress(input: $input) {
      ...OrderProps
      ${promotionsEnabled ? '...PromotionProps' : ''}
    }
  }
  ${ORDER_PROPS}
  ${promotionsEnabled ? PROMOTION_PROPS : ''}
`

const getCreateGuestOrderMutation = (promotionsEnabled: boolean) => gql`
  mutation createGuestOrderWithShippingAddress($input: CreateGuestOrderWithShippingAddressInput!, $shippingAddressCount: Int) {
    createGuestOrderWithShippingAddress(input: $input) {
      ...OrderProps
      ${promotionsEnabled ? '...PromotionProps' : ''}
    }
  }
  ${ORDER_PROPS}
  ${promotionsEnabled ? PROMOTION_PROPS : ''}
`

const getChangeShippingLevelMutation = (promotionsEnabled: boolean) => gql`
  mutation changeShippingLevel($input: ChangeShippingServiceLevelInput!, $shippingAddressCount: Int) {
    changeShippingServiceLevel(input: $input) {
      ...OrderProps
      ${promotionsEnabled ? '...PromotionProps' : ''}
    }
  }
  ${ORDER_PROPS}
  ${promotionsEnabled ? PROMOTION_PROPS : ''}
`

const getUser = gql`
  query getUser {
    user {
      id
      fullName
      email
      sha1Email
      isGuest
    }
    isLoggedIn
  }
`

interface IUseShippingAddressesProps {
  dispatch: Dispatch<IAction | IOrderSuccessAction | IErrorAction>
  order?: PartialOrder
}
interface IPhone {
  display: string
  value: string
}
export interface IShippingAddress {
  id?: number
  city: string
  countryCode: string
  name: string
  address1: string
  address2?: string
  postalCode?: string
  phone?: IPhone
  state: string
}
export interface IGuestShippingAddress extends IShippingAddress {
  email: string
  isNewsletterActive: boolean
}
interface IUseShippingAddresses {
  changeShippingServiceLevel(shippingServiceLevel: string): void
  connectAddressToOrder(addressId: number): void
  createShippingAddress(
    address: IShippingAddress,
    formikActions: FormikHelpers<IShippingAddress>,
  ): void
  createGuestOrderWithShippingAddress(
    address: IGuestShippingAddress,
    formikActions: FormikHelpers<IGuestShippingAddress>,
  ): void
}

const useShippingAddresses = ({
  dispatch,
  order,
}: IUseShippingAddressesProps): IUseShippingAddresses => {
  const router = useRouter()
  const { locale } = router
  const orderNumber = order?.orderNumber
  const isGuestCheckout = useFeatureFlag(FeatureFlag.GUEST_CHECKOUT)
  const promosEnabled = useFeatureFlag(FeatureFlag.PROMO_CODES_USER)
  const { logoutUser } = useAuth()

  const [changeShippingServiceLevelMutation] = useMutation(
    getChangeShippingLevelMutation(promosEnabled),
  )
  const [connectAddressToOrderMutation] = useMutation(
    getConnectAddressToOrderMutation(promosEnabled),
  )
  const [createShippingAddressMutation] = useMutation(
    getCreateShippingAddressMutation(promosEnabled),
  )
  const [createGuestOrderWithShippingAddressMutation] = useMutation(
    getCreateGuestOrderMutation(promosEnabled),
    {
      refetchQueries: () => [{ query: getUser }],
    },
  )

  const connectAddressToOrder = async (addressId: number) => {
    if (!addressId) {
      Bugsnag.notify(new Error(`No address id for selected address, order number: ${orderNumber}`))
      dispatch({
        type: 'AddressRequestFailure',
        payload: t(
          `${TP}.addressRequestFailure1`,
          'Unable to select address, please try a different address or add a new one.',
        ),
      })
      return
    }
    if (!orderNumber) {
      Bugsnag.notify(new Error(`No order number when selecting address, address id: ${addressId}`))
      dispatch({
        type: 'AddressRequestFailure',
        payload: t(
          `${TP}.addressRequestFailure2`,
          'There was an issue storing your order, please return to the product page.',
        ),
      })
      return
    }
    dispatch({ type: 'AddressRequest' })
    try {
      const { data } = await connectAddressToOrderMutation({
        variables: {
          input: { addressId, orderNumber },
          shippingAddressCount: SHIPPING_ADDRESS_COUNT,
        },
      })
      dispatch({ type: 'AddressRequestSuccess', payload: data.connectAddressToOrder })
    } catch (error) {
      if (isTimeoutError(error)) {
        dispatch({ type: 'orderRequestFailure', payload: timeoutMessage() })
        await logoutUser()
        return
      }
      dispatch({ type: 'AddressRequestFailure', payload: error.message })
    }
  }

  const createShippingAddress = async (
    address: IShippingAddress,
    formikActions: FormikHelpers<IShippingAddress>,
  ) => {
    if (!orderNumber) {
      Bugsnag.notify(new Error(`No order number when creating new address`))
      dispatch({
        type: 'orderRequestFailure',
        payload: t(
          `${TP}.orderRequestFailure`,
          'There was an issue storing your order, please return to the product page.',
        ),
      })
      return
    }
    dispatch({ type: 'AddressRequest' })
    try {
      const { data } = await createShippingAddressMutation({
        variables: {
          input: {
            ...pickBy(address),
            orderNumber,
          },
          shippingAddressCount: SHIPPING_ADDRESS_COUNT,
        },
      })
      dispatch({ type: 'AddressRequestSuccess', payload: data.createCheckoutShippingAddress })
    } catch (error) {
      if (isTimeoutError(error)) {
        dispatch({ type: 'orderRequestFailure', payload: timeoutMessage() })
        await logoutUser()
        return
      }
      const errorMessage = error.message.replace('GraphQL error: ', '')
      if (addressErrorFieldMap().has(errorMessage)) {
        formikActions.setErrors({ [addressErrorFieldMap().get(errorMessage)!]: errorMessage })
        dispatch({
          type: 'AddressRequestFailure',
          payload: t(`${TP}.addressRequestFailure3`, 'Please update the fields below'),
        })
        return
      }
      formikActions.setFieldError(
        'address1',
        t(`${TP}.addressFieldError`, 'Please update your address'),
      )
      dispatch({
        type: 'AddressRequestFailure',
        payload: errorMessage,
      })
    }
  }

  const createGuestOrderWithShippingAddress = async (
    address: IGuestShippingAddress,
    formikActions: FormikHelpers<IGuestShippingAddress>,
  ) => {
    dispatch({ type: 'AddressRequest' })
    if (!isGuestCheckout) {
      dispatch({
        type: 'AddressRequestFailure',
        payload: t(`${TP}.addressRequestFailure4`, 'Guest checkout is not available at this time'),
      })
    }
    const queryParams = qs.parse(router.asPath.split('?')[1])
    const reduceAcc: { [index: string]: any } = {}
    const input =
      queryParams &&
      Object.entries({
        ...address,
        expectedPriceCents: Number(queryParams.expectedPriceCents),
        instantShip: queryParams.instantShip === 'true',
        productId: Number(queryParams.productId),
        productTemplateId: Number(queryParams.productTemplateId),
        size: Number(queryParams.size),
        locale,
      }).reduce((acc, entry) => {
        // pass null if value is falsey and not an actual boolean
        const [key, value] = entry
        acc[key] = !!value || typeof value === 'boolean' ? value : null
        return acc
      }, reduceAcc)
    try {
      const { data } = await createGuestOrderWithShippingAddressMutation({
        variables: {
          input,
          shippingAddressCount: SHIPPING_ADDRESS_COUNT,
        },
      })
      dispatch({ type: 'AddressRequestSuccess', payload: data.createGuestOrderWithShippingAddress })
      const guestOrderNumber = data.createGuestOrderWithShippingAddress.orderNumber
      router.replace(`/checkout?orderNumber=${guestOrderNumber}`, `/checkout/${guestOrderNumber}`, {
        locale: router.locale,
        shallow: true, // this is important to prevent a full page reload
      })
    } catch (error) {
      const errorMessage = error.message.replace('GraphQL error: ', '')
      if (addressErrorFieldMap().has(errorMessage)) {
        formikActions.setErrors({ [addressErrorFieldMap().get(errorMessage)!]: errorMessage })
        dispatch({
          type: 'AddressRequestFailure',
          payload: t(`${TP}.addressRequestFailure5`, 'Please update the fields below'),
        })
        return
      }
      if (isTimeoutError(error)) {
        dispatch({ type: 'orderRequestFailure', payload: timeoutMessage() })
        await logoutUser()
        return
      }
      dispatch({
        type: 'AddressRequestFailure',
        payload: errorMessage,
      })
    }
  }

  const changeShippingServiceLevel = async (shippingServiceLevel: string) => {
    if (shippingServiceLevel === order?.shippingServiceLevel?.id) {
      const dispatchServiceLevelUpdate = dispatch as Dispatch<IOrderSuccessAction>
      return dispatchServiceLevelUpdate({
        type: 'ShippingServiceLevelRequestSuccess',
        payload: order as IOrder,
      })
    }
    dispatch({ type: 'ShippingServiceLevelRequest' })
    if (!orderNumber) {
      Bugsnag.notify(new Error(`No order number when changing shipping service level`))
      return dispatch({
        type: 'ShippingServiceLevelRequestFailure',
        payload: t(
          `${TP}.shippingServiceLevelRequestFailure`,
          'There was an issue storing your order, please return to the product page.',
        ),
      })
    }
    if (!shippingServiceLevel) {
      Bugsnag.notify(new Error(`No shipping service level selected`))
      return dispatch({
        type: 'ShippingServiceLevelRequestFailure',
        payload: t(
          `${TP}.shippingServiceLevelRequestFailure2`,
          'There was an issue selecting shipping service level. Please return to the shipping address panel.',
        ),
      })
    }
    try {
      const { data } = await changeShippingServiceLevelMutation({
        variables: {
          input: {
            shippingServiceLevel,
            orderNumber,
          },
          shippingAddressCount: SHIPPING_ADDRESS_COUNT,
        },
      })
      dispatch({
        type: 'ShippingServiceLevelRequestSuccess',
        payload: data.changeShippingServiceLevel,
      })
    } catch (error) {
      if (isTimeoutError(error)) {
        dispatch({ type: 'orderRequestFailure', payload: timeoutMessage() })
        await logoutUser()
        return
      }
      dispatch({
        type: 'ShippingServiceLevelRequestFailure',
        payload: error.message,
      })
    }
  }

  return {
    changeShippingServiceLevel,
    connectAddressToOrder,
    createShippingAddress,
    createGuestOrderWithShippingAddress,
  }
}

export default useShippingAddresses
